import { AclType } from "presentation/constant/ANAInfo/NbisivPermission";
import { Permission } from "presentation/constant/ANAInfo/PermissionName";
import { ChargeWaiverConstant } from "presentation/constant/ChargeWaiver/ChargeWaiverConstant";
import { WorkspaceConstant } from "presentation/constant/WorkSpaceConstant";
import { useChargeWaiverVM } from "presentation/hook/ChargeWaiver/useChargeWaiverVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useChargeWaiverTracked } from "presentation/store/ChargeWaiver/ChargeWaiverProvider";
import { isPermissionExist } from "presentation/utils/permissionUtils";
import { HeaderTitle } from "presentation/view/components/HeaderWithBackButton/HeaderTitle";
import { OverflowMenuButton } from "presentation/view/components/OverflowMenuButton";
import { memo, useCallback, useMemo } from "react";
import { HPHButton, IconButton } from "veronica-ui-component/dist/component/core";
import { Sidebarheader, StyledAction } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";

const ChargeWaiverTitleBar: React.FC = () => {
    const [chargeWaiverState] = useChargeWaiverTracked();
    const chargeWaiverVM = useChargeWaiverVM();
    const { selectedRows, searchCriteria, currentSelectedData, isShowEditPanel, isShowBulkEditPanel, isSubmit, isApproval, isShowReversePanel, isShowRejectPanel } = chargeWaiverState;
    const CHARGE_WAIVER_CONSTANT = ChargeWaiverConstant.Title;
    const messageBarVM = useMessageBarVM();
    const [anaInfoState] = useANAInfoTracked();
    const { operatingUnit, allowUpdate } = anaInfoState;
    const allowUpdateReject = isPermissionExist(Permission.CHARGE_WAIVER_REJECT, AclType.UPDATE, anaInfoState.defaultOperatingCompany, anaInfoState.allNbisivPermission);
    const allowUpdateApprove = isPermissionExist(Permission.CHARGE_WAIVER_APPROVE, AclType.UPDATE, anaInfoState.defaultOperatingCompany, anaInfoState.allNbisivPermission);

    const handleSearch = useCallback(async () => {
        chargeWaiverVM.onSearchClick();
    }, [chargeWaiverVM])

    const handleSubmit = useCallback(async () => {
        if (selectedRows.length === 0) {
            messageBarVM.showError("Please select record(s).");
            return;
        }
        chargeWaiverVM.onShowLoading();
        chargeWaiverVM.onSubmit(selectedRows).then(async (data) => {
            if (data && data.success) {
                if (data.data && data.data !== 'OK') {
                    chargeWaiverVM.onHideLoading();
                    messageBarVM.showError(data.data);
                } else {
                    chargeWaiverVM.onHideLoading();
                    messageBarVM.showSuccess("Submit Successfully.");
                    await chargeWaiverVM.searchChargeWaiverData(searchCriteria);
                }
            } else {
                chargeWaiverVM.onHideLoading();
                messageBarVM.showError("Submit Failed.");
            }
        })
    }, [messageBarVM, selectedRows, chargeWaiverVM, searchCriteria])

    const handleReverse = useCallback(async () => {
        if (selectedRows.length === 0) {
            messageBarVM.showError("Please select record(s).");
            return;
        }
        chargeWaiverVM.onReverseClick();
    }, [messageBarVM, selectedRows, chargeWaiverVM])

    const handleReject = useCallback(async () => {
        if (selectedRows.length === 0) {
            messageBarVM.showError("Please select record(s).");
            return;
        }
        chargeWaiverVM.onRejectClick();
    }, [messageBarVM, selectedRows, chargeWaiverVM]);

    const handleApprove = useCallback(async () => {
        if (selectedRows.length === 0) {
            messageBarVM.showError("Please select record(s).");
            return;
        }
        chargeWaiverVM.onShowLoading();
        chargeWaiverVM.onApprove(selectedRows).then(async (data) => {
            if (data && data.success) {
                if (data.data && data.data !== 'OK') {
                    chargeWaiverVM.onHideLoading();
                    messageBarVM.showError(data.data);
                } else {
                    chargeWaiverVM.onHideLoading();
                    messageBarVM.showSuccess("Approve Successfully.");
                    await chargeWaiverVM.searchChargeWaiverApprovalData(searchCriteria);
                }
            } else {
                chargeWaiverVM.onHideLoading();
                messageBarVM.showError("Approve Failed.");
            }
        })
    }, [messageBarVM, selectedRows, chargeWaiverVM, searchCriteria])

    const handleBulkEdit = useCallback(async () => {
        chargeWaiverVM.onBulkEditClick(currentSelectedData);
    }, [chargeWaiverVM, currentSelectedData])

    const memoCommonAccountMenu = useMemo(() => {
        return [
            {
                title: ChargeWaiverConstant.Title.BULK_EDIT,
                disabled: false,
                command: () => {
                    handleBulkEdit();
                }
            }
        ]
    }, [handleBulkEdit]);

    const memoMoreIconButton = useMemo(() => {
        let disabled: boolean = false;
        if (selectedRows.length === 0 || isApproval) {
            disabled = true;
        }
        return <OverflowMenuButton buttonLabel={WorkspaceConstant.Common.BUTTON_MORE} disabled={disabled}
            toolTipText={WorkspaceConstant.Common.BUTTON_MORE} navigationList={memoCommonAccountMenu} buttonSize={"Small"} buttonTheme={"Secondary"} />
    }, [selectedRows, memoCommonAccountMenu, isApproval])

    const isDisabled = useCallback(() => {
        if (selectedRows.length === 0 || isShowEditPanel || isShowBulkEditPanel || isApproval) {
            return true;
        }
        return false;
    }, [isShowEditPanel, isShowBulkEditPanel, isApproval, selectedRows]);

    const isDisabledSearch = useCallback(() => {
        if (isShowReversePanel || isShowRejectPanel) {
            return true;
        }
        return false;
    }, [isShowReversePanel, isShowRejectPanel]);

    const isDisabledApproval = useCallback(() => {
        if (selectedRows.length === 0 || isShowEditPanel || isShowBulkEditPanel || isSubmit) {
            return true;
        }
        return false;
    }, [isShowEditPanel, isShowBulkEditPanel, isSubmit, selectedRows]);

    return <Sidebarheader style={{ width: '100%', display: "flex", alignItems: "center" }}>
        <HeaderTitle>{CHARGE_WAIVER_CONSTANT.WAIVER_APPLICATION}</HeaderTitle>
        <StyledAction className="tm-animated-wrapper">
            {<IconButton fileName='Icon-search' disabled={isDisabledSearch()} size='medium' toolTipText={'Search'} toolTipArrow={false} onClick={handleSearch} />}
            {allowUpdate && <>
                <div className="add-seperator" />
                <HPHButton disabled={isDisabled()} label={CHARGE_WAIVER_CONSTANT.SUBMIT} size={'Small'} theme={'Secondary'} onClick={handleSubmit} />
                {(operatingUnit === 'SAPT' || operatingUnit === 'KICT' || allowUpdateReject || allowUpdateApprove) && <div className="add-seperator" />}
                {(operatingUnit === 'SAPT' || operatingUnit === 'KICT') && <HPHButton disabled={isDisabledApproval()} label={CHARGE_WAIVER_CONSTANT.REVERSE} size={'Small'} theme={'Secondary'} onClick={handleReverse} />}
                {allowUpdateReject && <HPHButton disabled={isDisabledApproval()} label={CHARGE_WAIVER_CONSTANT.REJECT} size={'Small'} theme={'Secondary'} onClick={handleReject} />}
                {allowUpdateApprove && <HPHButton disabled={isDisabledApproval()} label={CHARGE_WAIVER_CONSTANT.APPROVE} size={'Small'} theme={'Primary'} onClick={handleApprove} />}
                {(operatingUnit === 'SAPT' || operatingUnit === 'KICT') && <><div className="add-seperator" />{memoMoreIconButton}</>}
            </>}
        </StyledAction>
        {chargeWaiverState.forceRefresh && <></>}
    </Sidebarheader>
}

export default memo(ChargeWaiverTitleBar);